import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import CircularProgress from '@mui/material/CircularProgress';
import Flex from '../../../components/base/Flex';
import OutlinedButton from '../../../components/base/OutlinedButton';
import ContainedButton from '../../../components/base/ContainedButton';
import CustomModal from '../../../components/base/CustomModal';
import { toast } from 'react-toastify';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useScandiumMutation, useScandiumQuery } from '../../../data-layer/utils';
import TextField from '@mui/material/node/TextField';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';

const STEPS = {
  TOKEN: 0,
  COMPANY: 1,
  PROJECT: 2,
  COMPLETE: 3
};

const STEP_LABELS = ['Add API Token', 'Select Company', 'Select Project', 'Complete'];

export const SelectDropdown = ({
  value,
  options,
  onChange,
  isLoading = false,
  label,
  labelId,
  disabled = false,
  ...otherProps
}) => {
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled || isLoading}
        IconComponent={(defaultProps) =>
          isLoading ? (
            <CircularProgress size={16} color="secondary" {...defaultProps} />
          ) : (
            <ArrowDropDownIcon {...defaultProps} />
          )
        }
        renderValue={(selectedValue) => {
          if (!selectedValue) return '';
          return selectedValue?.name || selectedValue?.login || selectedValue?.title || '';
        }}
        MenuProps={{
          elevation: 1
        }}
        {...otherProps}>
        {options?.map((item) => (
          <MenuItem key={item.id} value={item}>
            {item?.name || item?.login || item?.title}
          </MenuItem>
        ))}
        {(!options || options.length === 0) && !isLoading && (
          <MenuItem disabled>No options available</MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

const TestPodConnectModal = ({
  open,
  onClose,
  onComplete,
  updateChannelData,
  successChannels,
  errorChannels,
  activeTab,
  showTestPodSettings,
  handlePostChannels
}) => {
  const [currentStep, setCurrentStep] = useState(STEPS.TOKEN);
  const [testpodToken, setTestPodToken] = useState('');
  const [selectedTestPodCompany, setSelectedTestPodCompany] = useState(null);
  const [selectedTestPodCompanyProject, setSelectedTestPodCompanyProject] = useState(null);

  const handleClose = () => {
    setCurrentStep(STEPS.TOKEN);
    setTestPodToken('');
    setSelectedTestPodCompany(null);
    setSelectedTestPodCompanyProject(null);
    onClose();
  };

  const { mutateAsync: postTestPodToken, isLoading: isTokenLoading } = useScandiumMutation(
    '/third-parties/testpod/api-token',
    {
      onSuccess: () => {
        setCurrentStep(STEPS.COMPANY);
        toast.success('Token verified successfully!');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to verify token');
      }
    }
  );

  const {
    data: companiesData = [],
    isLoading: isLoadingCompanies,
    refetch: refetchCompanies
  } = useScandiumQuery('/third-parties/testpod/companies', {
    enabled: false,
    onSuccess: () => {
      if (currentStep === STEPS.COMPANY) {
        toast.success('Companies loaded successfully');
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to load companies');
    }
  });

  const { data: projectsData = [], isLoading: isLoadingProjects } = useScandiumQuery(
    `/third-parties/testpod/companies/${selectedTestPodCompany?.id}/projects`,
    {
      enabled: !!selectedTestPodCompany?.id && currentStep >= STEPS.PROJECT,
      onSuccess: () => {
        if (currentStep === STEPS.PROJECT) {
          toast.success('Projects loaded successfully');
        }
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to load projects');
      }
    }
  );

  const handleTokenSubmit = async () => {
    if (!testpodToken.trim()) {
      toast.error('Please enter a valid TestPod token');
      return;
    }

    try {
      await postTestPodToken({ api_token: testpodToken });
      await refetchCompanies();
    } catch (error) {
      console.error('Token submission error:', error);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedTestPodCompany(company);
    setSelectedTestPodCompanyProject(null);
    setCurrentStep(STEPS.PROJECT);
  };

  const handleProjectSelect = (project) => {
    setSelectedTestPodCompanyProject(project);
  };

  const handleSaveConnection = async () => {
    if (!selectedTestPodCompany || !selectedTestPodCompanyProject) {
      toast.error('Please select both company and project');
      return;
    }

    try {
      updateChannelData(
        'testpod',
        {
          company_id: selectedTestPodCompany.id,
          project_id: selectedTestPodCompanyProject.id,
          company_name: selectedTestPodCompany.name,
          name: selectedTestPodCompanyProject.name
        },
        handlePostChannels
      );

      setCurrentStep(STEPS.COMPLETE);
      toast.success('TestPod connected successfully!');

      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      toast.error('Failed to save connection');
    }
  };

  const canProceedFromToken = !isTokenLoading && testpodToken.trim().length > 0;
  const canProceedFromCompany = !!selectedTestPodCompany;
  const canProceedFromProject = !!selectedTestPodCompanyProject;

  const getStepContent = (step) => {
    switch (step) {
      case STEPS.TOKEN:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter your TestPod API token to get started. You can find this in your TestPod user
              profile.
            </Typography>
            <TextField
              label="TestPod API Token"
              variant="outlined"
              type="password"
              fullWidth
              value={testpodToken}
              onChange={(e) => setTestPodToken(e.target.value)}
              size="small"
              placeholder="Enter your API token"
              disabled={isTokenLoading}
            />
          </Box>
        );

      case STEPS.COMPANY:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select the company you want to connect from your TestPod account.
            </Typography>
            <SelectDropdown
              options={companiesData.data || []}
              isLoading={isLoadingCompanies}
              onChange={(e) => handleCompanySelect(e.target.value)}
              value={selectedTestPodCompany}
              label="Select Company"
              labelId="company-select-label"
            />
          </Box>
        );

      case STEPS.PROJECT:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a project from {selectedTestPodCompany?.name}.
            </Typography>
            <SelectDropdown
              options={projectsData.data || []}
              isLoading={isLoadingProjects}
              onChange={(e) => handleProjectSelect(e.target.value)}
              value={selectedTestPodCompanyProject}
              label="Select Project"
              labelId="project-select-label"
            />
          </Box>
        );

      case STEPS.COMPLETE:
        return (
          <Box textAlign="center" py={2}>
            <Typography variant="h6" color="success.main" gutterBottom>
              ✅ Connected Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              TestPod is now connected to {selectedTestPodCompany?.name} -{' '}
              {selectedTestPodCompanyProject?.name}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepActions = (step) => {
    switch (step) {
      case STEPS.TOKEN:
        return (
          <Flex columnGap={2} justifyContent="flex-end">
            <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
            <ContainedButton
              onClick={handleTokenSubmit}
              disabled={!canProceedFromToken}
              loading={isTokenLoading}>
              Verify Token
            </ContainedButton>
          </Flex>
        );

      case STEPS.COMPANY:
        return (
          <Flex columnGap={2} justifyContent="space-between">
            <OutlinedButton onClick={() => setCurrentStep(STEPS.TOKEN)}>Back</OutlinedButton>
            <Flex columnGap={2}>
              <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
              <ContainedButton
                onClick={() => handleCompanySelect(selectedTestPodCompany)}
                disabled={!canProceedFromCompany}>
                Next
              </ContainedButton>
            </Flex>
          </Flex>
        );

      case STEPS.PROJECT:
        return (
          <Flex columnGap={2} justifyContent="space-between">
            <OutlinedButton onClick={() => setCurrentStep(STEPS.COMPANY)}>Back</OutlinedButton>
            <Flex columnGap={2}>
              <OutlinedButton onClick={handleClose}>Cancel</OutlinedButton>
              <ContainedButton onClick={handleSaveConnection} disabled={!canProceedFromProject}>
                Connect
              </ContainedButton>
            </Flex>
          </Flex>
        );

      case STEPS.COMPLETE:
        return (
          <Flex columnGap={2} justifyContent="flex-end">
            <ContainedButton onClick={handleClose}>Done</ContainedButton>
          </Flex>
        );

      default:
        return null;
    }
  };

  return (
    <CustomModal open={open} onClose={handleClose}>
      <Box py={2} px={2} width="80vw" maxWidth="500px">
        <Typography variant="h4" color="primary" gutterBottom>
          Connect Your TestPod Account
        </Typography>

        <Typography variant="body1" color="text.secondary" mb={3}>
          {`Connect your TestPod account to track test results for ${
            activeTab === 0 ? 'success' : 'errors'
          }.`}
        </Typography>

        <Stepper activeStep={currentStep} orientation="vertical" sx={{ mb: 3 }}>
          {STEP_LABELS.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {getStepContent(index)}
                <Box sx={{ mt: 2 }}>{getStepActions(index)}</Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </CustomModal>
  );
};

export default TestPodConnectModal;
